import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Textarea
} from '@invent/wl-ui-kit-next/ui';
import { ChevronDown } from 'lucide-react';
import * as React from 'react';

import {
  ChoiceSelect,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  NotPermitted
} from '@/design';
import { ApiError } from '@/domains/api';
import { cyclesApi, type IRankingCycle } from '../../api/cycles-api';
import type {
  IListAssignee,
  IListEditRefusal,
  IListField,
  IListQuery,
  IListRow
} from '../../api/lists-contracts';
import { listsApi } from '../../api/lists-api';
import { useAdminData } from '../../components/admin-page';
import { NOT_ARRIVED } from '../../components/field-controls';
import { RecycleRunDialog } from '../../components/recycle-run';
import { refusedCodeShort } from '../../correction-refusal';
import type { InternalUiValue } from '../../internal-field-values';
import { internalMalformedHint } from '../../internal-field-values';
import {
  ALL_SURVEYS,
  countOf,
  listSurveyLabel,
  TEAMS_SURVEY,
  type ICatalogueSection
} from '../../lists';
import type { IRecycleRun, IRecycleTarget } from '../../recycle-bin';
import { refusalMessage } from '../../refusal';
import { accessWords, pinAccessOf } from '../cycles-page';
import { ValueControl } from './cells';
import {
  ASSIGNED_TO,
  answerTagOf,
  editKindOf,
  nameOf,
  stagedOf,
  storedUi,
  type CellEditKind
} from './editing';
import { FieldPicker } from './field-picker';

/**
 * A list's *Actions ▾* (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md`
 * §3.10): **Set a field on the selected rows…** stages one pending cell per ticked row — counted on
 * *Save data*, saved in its one batch (LST-02 AC2); **Assign the selected rows to…** saves its cells
 * at once, as *Save data* would, leaving any other staged cell staged (SR-491, LST-11); and
 * **Set a field on every row of this list…** is the mass update over the **query**, not the page
 * (`lists.bulk`): sent at once, `{viewId, note, query, set}`, and its answer said in words.
 *
 * **A mass update judges nothing it can send**: past 2,000 rows survey-api refuses it (its
 * `MassUpdateLimit`), so the dialog says so off the read's `total` before the press — a warning, the
 * press withheld — and survey-api's 400 is said in its words all the same, since the list may have
 * grown since. A conflict there is said by name and count, never a dialog per cell.
 *
 * Every entry is drawn whatever the reader holds; one their roles do not reach is disabled with
 * `NotPermitted`'s sentence (§3.12). **An answer is set like a staff field** (SR-403) — survey-api
 * corrects it, `responses.annotate` beside the route's own — and a mass update's answer cells it
 * could not correct (a draft's, an answer the response's logic hides…) are counted by why and
 * named where the page holds them; where nothing landed, the dialog stays with the value typed.
 * **Export…** (the dialog, §3.11) and **Save as a report…** (a report with this list's query, the
 * owner's Q7) are every reader's, `responses.browse` — the screen's own — and open the screen's
 * dialogs.
 *
 * **Move the selected rows to the recycle bin…** (SR-470's D6, legacy's "Bulk Recycle") moves each
 * ticked row's response, one call per response (`RecycleRunDialog`), under `responses.annotate`. A
 * row's `revision` is the list row's, not its response's, so each response is read just before its
 * move (the owner's Q8 (a)). It is held while cells are staged: a moved row leaves the list on the
 * re-read, and its staged cells with it.
 *
 * **Add to a Teams access list…** and **Remove from a Teams access list…** (SR-487 ruling 17,
 * survey-api's PFR §6) write the ticked rows' participants to one TEAMS pin's access list, the pin
 * chosen in the dialog from the cycles that pin TEAMS and are not archived. The write is
 * `rankings.manage`'s and the cycles' read `cycles.manage`'s, so the entries need both. survey-api
 * refuses the whole write when a participant is not a team — 400 at `participantIds[i]` — and the
 * dialog names each refused row by its place in what was sent.
 */

/** survey-api's `ListEditService.MassUpdateLimit`: the most rows one mass update may match. */
export const MASS_UPDATE_LIMIT = 2000;

type SetMode = 'selected' | 'assign' | 'every';

type AccessMode = 'add' | 'remove';

export function ListActions({
  part = 'list',
  survey,
  query,
  viewId,
  total,
  catalogue,
  selectedRows,
  pageRows,
  accounts,
  canWrite,
  canAssign,
  canBulk,
  canRecycle,
  canAccess,
  canReadCycles,
  staged,
  saving,
  answersLocked = false,
  accountsUnread = false,
  onStage,
  onSaveNow,
  onMassUpdated,
  onRecycled,
  onAccessChanged,
  onExport,
  onSaveAsReport
}: {
  /** Which half (SR-412): *Actions ▾* over the whole list, or the selection bar's presses on the
   *  ticked rows. Each half draws the dialogs its presses open. */
  part?: 'list' | 'selection';
  survey: string;
  /** The screen's query — what a mass update matches. */
  query: IListQuery;
  viewId: Nullable<string>;
  /** The read's `total` — what the mass update's dialog counts. */
  total: Nullable<number>;
  /** The editable fields, in the catalogue's groups and order. */
  catalogue: readonly ICatalogueSection[] & { hidden?: number };
  selectedRows: readonly IListRow[];
  /** The page's rows — what a mass update's answer names the rows it found by. */
  pageRows: readonly IListRow[];
  accounts: readonly IListAssignee[];
  canWrite: boolean;
  canAssign: boolean;
  canBulk: boolean;
  /** `responses.annotate` — a move to the recycle bin. */
  canRecycle: boolean;
  /** `rankings.manage` — a write to a Teams access list. */
  canAccess: boolean;
  /** `cycles.manage` — the cycles' read, from which a Teams access list is chosen. */
  canReadCycles: boolean;
  /** The grid holds staged cells: no row is moved until they are saved or discarded. */
  staged: boolean;
  /** A save is out: an assignment, which saves at once, waits for its answer. */
  saving: boolean;
  /** The catalogue's answers are editable, and the reader's roles do not hold `responses.annotate`:
   *  they are left out of the fields offered, and the reason is said. */
  answersLocked?: boolean;
  /** The accounts to assign could not be read — nothing is assigned until they are. */
  accountsUnread?: boolean;
  /** Stages the value on each row — the grid's drafts. */
  onStage: (rows: readonly IListRow[], field: IListField, ui: InternalUiValue) => void;
  /** Saves the value on each row at once — *Assign…*'s press. */
  onSaveNow: (rows: readonly IListRow[], field: IListField, ui: InternalUiValue) => void;
  /** A mass update's answer in words; the screen then reads the list again. */
  onMassUpdated: (said: string) => void;
  /** A move to the recycle bin's outcome: the screen says it and reads the page afresh. */
  onRecycled: (run: IRecycleRun) => Promise<unknown>;
  /** A Teams access list's change in words; the list's rows do not move. */
  onAccessChanged: (said: string) => void;
  onExport: () => void;
  onSaveAsReport: () => void;
}) {
  const [open, setOpen] = React.useState<Nullable<SetMode>>(null);
  const [recycling, setRecycling] = React.useState(false);
  const [access, setAccess] = React.useState<Nullable<AccessMode>>(null);
  const reasonId = React.useId();
  // The count of fields an administrator hid rides along, for the picker's note (SR-462).
  const editable = Object.assign(
    catalogue
      .map((section) => ({
        ...section,
        fields: section.fields.filter(
          (field) => editKindOf(field) !== null && (field.code !== ASSIGNED_TO || (canAssign && !accountsUnread))
        )
      }))
      .filter((section) => section.fields.length > 0),
    { hidden: catalogue.hidden }
  );
  const assignee = editable.flatMap((section) => section.fields).find((field) => field.code === ASSIGNED_TO);
  const none = selectedRows.length === 0;
  const allSurveys = survey === ALL_SURVEYS.code;

  // The menu says what the reader's roles keep from the whole list; a selection's press says its own
  // on hover.
  const listReasons = [
    !canWrite ? 'editing cells in a list' : null,
    canWrite && answersLocked ? 'correcting survey answers' : null,
    canWrite && !canBulk ? 'setting a field on every row of a list' : null
  ].filter((what): what is string => what !== null);
  const accessOff = !canAccess || !canReadCycles || none;

  const press = (label: string, off: boolean, why: Nullable<string>, onPress: () => void) => (
    <Button
      key={label}
      type="button"
      size="sm"
      variant="outline"
      className="h-7 bg-white font-normal"
      disabled={off}
      title={why ?? undefined}
      onClick={onPress}
    >
      {label}
    </Button>
  );
  const head =
    part === 'selection' ? (
      <>
        {press('Set a field…', !canWrite, canWrite ? null : 'Your Team Portal roles do not include editing cells in a list', () => setOpen('selected'))}
        {press(
          'Assign to…',
          !canWrite || !canAssign || accountsUnread || saving,
          !canWrite || !canAssign
            ? 'Your Team Portal roles do not include assigning rows'
            : accountsUnread
              ? 'The accounts to assign could not be read'
              : null,
          () => setOpen('assign')
        )}
        {press(
          'Move to the recycle bin…',
          !canRecycle || staged,
          !canRecycle
            ? 'Your Team Portal roles do not include moving responses to the recycle bin'
            : staged
              ? 'Save or discard your changes first'
              : null,
          () => setRecycling(true)
        )}
        {/* The Teams access lists are a rarer press: under *More ▾*, so the bar keeps to one line. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" size="sm" variant="outline" className="h-7 gap-1 bg-white font-normal">
              More
              <ChevronDown className="size-3.5" aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {(['add', 'remove'] as const).map((mode) => (
              <DropdownMenuItem key={mode} disabled={accessOff} onSelect={() => setAccess(mode)}>
                <span className="grid">
                  {ACCESS_TITLES[mode]}…
                  {!canAccess && (
                    <span className="text-xs text-muted-foreground">
                      Your Team Portal roles do not include changing who can start a survey
                    </span>
                  )}
                  {canAccess && !canReadCycles && (
                    <span className="text-xs text-muted-foreground">
                      Your Team Portal roles do not include reading the survey cycles
                    </span>
                  )}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </>
    ) : (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" className="h-8 gap-1 px-2.5 font-normal">
            Actions
            <ChevronDown className="size-4" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80" aria-describedby={listReasons.length > 0 ? reasonId : undefined}>
          <DropdownMenuItem
            disabled={!canWrite || !canBulk || allSurveys || total === null}
            onSelect={() => setOpen('every')}
          >
            <span className="grid">
              Set a field on every row of this list…
              {canWrite && canBulk && allSurveys && (
                <span className="text-xs text-muted-foreground">
                  A mass update names one survey — open a survey’s tab
                </span>
              )}
            </span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={onExport}>Export…</DropdownMenuItem>
          <DropdownMenuItem onSelect={onSaveAsReport}>Save as a report…</DropdownMenuItem>
          {listReasons.length > 0 && (
            <div id={reasonId} className="grid gap-1 border-t border-border px-2 py-1.5">
              {listReasons.map((what) => (
                <NotPermitted key={what} what={what} className="text-xs" />
              ))}
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );

  return (
    <>
      {head}
      {open !== null && (
        <SetFieldDialog
          mode={open}
          sections={editable}
          assignee={assignee}
          rows={selectedRows}
          pageRows={pageRows}
          total={total ?? 0}
          query={query}
          viewId={viewId}
          accounts={accounts}
          onStage={onStage}
          onSaveNow={onSaveNow}
          onMassUpdated={onMassUpdated}
          onClose={() => setOpen(null)}
        />
      )}
      <RecycleRunDialog
        open={recycling}
        onOpenChange={setRecycling}
        move="recycle"
        targets={selectedRows.map(recycleTargetOf)}
        onRan={onRecycled}
      />
      {access !== null && (
        <AccessListDialog
          mode={access}
          rows={selectedRows}
          onChanged={onAccessChanged}
          onClose={() => setAccess(null)}
        />
      )}
    </>
  );
}

/** The two access entries' names, and their dialogs' titles. */
const ACCESS_TITLES: Record<AccessMode, string> = {
  add: 'Add to a Teams access list',
  remove: 'Remove from a Teams access list'
};

/** A TEAMS pin a list's selection can be written to: its cycle, and who the pin admits. */
interface ITeamsPin {
  cycleCode: string;
  cycleName: string;
  access: string;
}

/** The cycles' TEAMS pins, neither the cycle nor the pin archived, in survey-api's order. */
function teamsPinsOf(cycles: readonly IRankingCycle[]): ITeamsPin[] {
  return cycles.flatMap((cycle) =>
    cycle.status === 'archived'
      ? []
      : cycle.surveys
          .filter((pin) => pin.surveyCode === TEAMS_SURVEY && pin.status !== 'archived')
          .map((pin) => ({
            cycleCode: cycle.code,
            cycleName: cycle.name,
            access: pinAccessOf(pin.config)
          }))
  );
}

/** The dialog: one TEAMS pin chosen, the ticked rows' participants added to its access list or
 *  taken off it. A refusal stays in the dialog, each refused row named. */
function AccessListDialog({
  mode,
  rows,
  onChanged,
  onClose
}: {
  mode: AccessMode;
  rows: readonly IListRow[];
  onChanged: (said: string) => void;
  onClose: () => void;
}) {
  const cycles = useAdminData((signal) => cyclesApi.list(signal), []);
  const pins = cycles.data ? teamsPinsOf(cycles.data) : [];
  const [cycleCode, setCycleCode] = React.useState('');
  const [said, setSaid] = React.useState<Nullable<React.ReactNode>>(null);
  const [sending, setSending] = React.useState(false);
  const labelId = React.useId();
  // One id per participant, in the order ticked: a participant's rows are one entry on a list.
  const participants = [...new Map(rows.map((row) => [row.participantId, nameOf(row)]))];
  const pin = pins.find((candidate) => candidate.cycleCode === cycleCode);

  const send = async () => {
    if (!pin) return;
    setSending(true);
    setSaid(null);
    const ids = participants.map(([id]) => id);
    const list = `the access list of ${pin.cycleName}`;
    const besides = (n: number, words: string) =>
      n > 0 ? ` · ${n.toLocaleString('en-US')} ${words}` : '';
    try {
      if (mode === 'add') {
        const { added, already } = await cyclesApi.addAccess(pin.cycleCode, TEAMS_SURVEY, ids);
        onChanged(
          `${countOf(added.length, 'participant')} added to ${list}${besides(already.length, 'already on it')}`
        );
      } else {
        const { removed, absent } = await cyclesApi.removeAccess(pin.cycleCode, TEAMS_SURVEY, ids);
        onChanged(
          `${countOf(removed.length, 'participant')} removed from ${list}${besides(absent.length, 'not on it')}`
        );
      }
      onClose();
    } catch (failure) {
      setSending(false);
      const refused = refusedParticipants(failure, participants);
      setSaid(
        refused.length > 0 ? (
          <>
            Nothing was changed:
            <ul className="mt-1 list-disc pl-5">
              {refused.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </>
        ) : (
          refusalMessage(failure, 'The access list was not changed.')
        )
      );
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !sending && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{ACCESS_TITLES[mode]}</DialogTitle>
          <DialogDescription>
            {`${countOf(participants.length, 'participant is', 'participants are')} selected.`}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="gap-3 text-sm">
          {cycles.error ? (
            <p role="alert" className="text-destructive">
              The survey cycles could not be read — nothing can be chosen.
            </p>
          ) : !cycles.data ? (
            <p className="text-muted-foreground">Reading the survey cycles…</p>
          ) : pins.length === 0 ? (
            <p className="text-muted-foreground">No cycle pins the Teams survey.</p>
          ) : (
            <div className="grid gap-1">
              <span id={labelId} className="font-medium">
                Access list
              </span>
              <ChoiceSelect
                labelledBy={labelId}
                value={cycleCode}
                onChange={setCycleCode}
                disabled={sending}
                className="w-full"
                choices={pins.map((candidate) => ({
                  value: candidate.cycleCode,
                  label: `${candidate.cycleName} · ${accessWords(candidate.access)}`
                }))}
              />
            </div>
          )}
          {said && (
            <div role="alert" className="text-destructive">
              {said}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={sending} onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={!pin || sending} onClick={() => void send()}>
            {sending ? 'Saving…' : mode === 'add' ? 'Add' : 'Remove'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** survey-api's 400 at `participantIds[i]`, each said by the row's name — "Avery Stone is a
 *  person, and TEAMS asks about a team". Empty for any other refusal. */
function refusedParticipants(
  failure: unknown,
  participants: readonly (readonly [string, string])[]
): string[] {
  if (!(failure instanceof ApiError) || failure.status !== 400) return [];
  return (failure.problem?.problems ?? []).flatMap(({ target, reason }) => {
    const at = /^participantIds\[(\d+)\]$/.exec(target);
    const participant = at ? participants[Number(at[1])] : undefined;
    return participant ? [`${participant[1]} ${reason}`] : [];
  });
}

/** A ticked row as the recycle bin's run sends it: its response, read again before the move — the
 *  row's `revision` is the list row's own. */
function recycleTargetOf(row: IListRow): IRecycleTarget {
  return {
    responseId: row.responseId,
    revision: null,
    subject: { participant: nameOf(row), survey: listSurveyLabel(row.survey) }
  };
}

/** Why a mass update's answer cells were not corrected, counted by code — "2 drafts, 1 hidden by
 *  the response’s logic". */
function refusedWhy(refused: readonly IListEditRefusal[]): string {
  const byCode = new Map<string, number>();
  for (const { code } of refused) byCode.set(code, (byCode.get(code) ?? 0) + 1);
  return [...byCode].map(([code, count]) => refusedCodeShort(code, count)).join(', ');
}

/** The names of the rows a mass update found changed since — those on the page by name. */
function namedRows(ids: readonly string[], rows: readonly IListRow[]): string {
  const names = ids.flatMap((id) => {
    const row = rows.find((candidate) => candidate.participantId === id);
    return row ? [nameOf(row)] : [];
  });
  const rest = ids.length - names.length;
  const shown = names.slice(0, 3);
  const more = rest + (names.length - shown.length);
  if (shown.length === 0) return '';
  return ` (${shown.join(', ')}${more > 0 ? ` and ${more.toLocaleString('en-US')} more` : ''})`;
}

/** The dialog: a field, a value in that field's control — staged on the ticked rows, or sent over
 *  the query for every row. */
function SetFieldDialog({
  mode,
  sections,
  assignee,
  rows,
  pageRows,
  total,
  query,
  viewId,
  accounts,
  onStage,
  onSaveNow,
  onMassUpdated,
  onClose
}: {
  mode: SetMode;
  /** The editable fields offered, in their groups. */
  sections: readonly ICatalogueSection[];
  /** *Assigned to*, which *Assign…* sets. */
  assignee: IListField | undefined;
  rows: readonly IListRow[];
  pageRows: readonly IListRow[];
  total: number;
  query: IListQuery;
  viewId: Nullable<string>;
  accounts: readonly IListAssignee[];
  onStage: (rows: readonly IListRow[], field: IListField, ui: InternalUiValue) => void;
  onSaveNow: (rows: readonly IListRow[], field: IListField, ui: InternalUiValue) => void;
  onMassUpdated: (said: string) => void;
  onClose: () => void;
}) {
  const fields = sections.flatMap((section) => section.fields);
  // *Assign…* sets the one field; every other mode starts with none chosen.
  const [code, setCode] = React.useState('');
  const field = mode === 'assign' ? assignee : fields.find((candidate) => candidate.code === code);
  const kind: Nullable<CellEditKind> = field ? editKindOf(field) : null;
  const [ui, setUi] = React.useState<InternalUiValue>(() => (kind ? storedUi(kind, null) : ''));
  const [note, setNote] = React.useState('');
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const [sending, setSending] = React.useState(false);
  const box = React.useRef<HTMLDivElement>(null);
  const every = mode === 'every';
  const tooMany = every && total > MASS_UPDATE_LIMIT;

  const choose = (next: string) => {
    setCode(next);
    const chosen = fields.find((candidate) => candidate.code === next);
    const nextKind = chosen ? editKindOf(chosen) : null;
    setUi(nextKind ? storedUi(nextKind, null) : '');
    setSaid(null);
  };

  const apply = async () => {
    if (!field || !kind) return;
    const badInput = box.current?.querySelector('input')?.validity.badInput === true;
    const staged = stagedOf(kind, ui, badInput, answerTagOf(field));
    if (staged.held) {
      setSaid(kind === 'assignee' ? 'Choose an account.' : internalMalformedHint(kind));
      return;
    }
    if (mode === 'assign') {
      onSaveNow(rows, field, ui);
      onClose();
      return;
    }
    if (!every) {
      onStage(rows, field, ui);
      onClose();
      return;
    }
    setSending(true);
    setSaid(null);
    try {
      const { viewId: _ignored, ...matched } = query;
      const answer = await listsApi.edit({
        viewId,
        note: note.trim() === '' ? null : note.trim(),
        query: matched,
        set: [{ field: field.code, value: staged.value }]
      });
      const conflicts = answer.conflicts.map((conflict) => conflict.participantId);
      const refused = answer.refused ?? [];
      const why = refused.length > 0 ? refusedWhy(refused) : '';
      if (answer.applied.length + answer.unchanged.length + conflicts.length === 0 && refused.length > 0) {
        // Nothing landed: said here, the field and the value kept to change and send again.
        setSending(false);
        setSaid(
          `Nothing was set — ${countOf(refused.length, 'row was', 'rows were')} not corrected (${why})${namedRows(refused.map((one) => one.participantId), pageRows)}.`
        );
        return;
      }
      onMassUpdated(
        [
          `${countOf(answer.matched ?? 0, 'row', 'rows')} matched`,
          `${answer.applied.length.toLocaleString('en-US')} set`,
          answer.unchanged.length > 0 ? `${answer.unchanged.length.toLocaleString('en-US')} already held the value` : null,
          conflicts.length > 0
            ? `${conflicts.length.toLocaleString('en-US')} changed by someone else since${namedRows(conflicts, pageRows)} — Refresh to see them`
            : null,
          refused.length > 0
            ? `${refused.length.toLocaleString('en-US')} not corrected (${why})${namedRows(refused.map((one) => one.participantId), pageRows)}`
            : null
        ]
          .filter(Boolean)
          .join(' · ')
      );
      onClose();
    } catch (error) {
      setSending(false);
      if (!(error instanceof ApiError) || error.status >= 500) {
        onMassUpdated(`${NOT_ARRIVED} Refresh to see whether the rows changed.`);
        onClose();
      } else if (error.status === 403) {
        setSaid('Your Team Portal roles do not allow a mass update — nothing was set.');
      } else {
        const problems = error.problem?.problems ?? [];
        setSaid(
          problems.length > 0
            ? `Nothing was set: ${problems.map((problem) => problem.reason).join('; ')}.`
            : `Nothing was set. ${error.problem?.title ?? error.message}`
        );
      }
    }
  };

  const title =
    mode === 'assign'
      ? 'Assign the selected rows'
      : every
        ? 'Set a field on every row of this list'
        : 'Set a field on the selected rows';

  return (
    <Dialog open onOpenChange={(open) => !open && !sending && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {every
              ? `${countOf(total, 'row matches', 'rows match')} this list. The value is written at once, to every one of them.`
              : mode === 'assign'
                ? `${countOf(rows.length, 'row is', 'rows are')} selected. They are assigned at once.`
                : `${countOf(rows.length, 'row is', 'rows are')} selected. Each takes a pending change — saved with Save data.`}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="gap-3 text-sm">
          {mode !== 'assign' && (
            <div className="grid gap-1">
              <span className="font-medium" aria-hidden>
                Field
              </span>
              <FieldPicker label="Field" sections={sections} value={code} onChoose={choose} className="w-full" />
            </div>
          )}
          {field && kind && (
            <div ref={box} className="grid gap-1">
              <span className="font-medium">{mode === 'assign' ? 'Assign to' : 'Value'}</span>
              <ValueControl
                key={field.code}
                kind={kind}
                field={field}
                label={mode === 'assign' ? 'Assign to' : `Value of ${field.label}`}
                ui={ui}
                onChange={(next) => {
                  setUi(next);
                  setSaid(null);
                }}
                accounts={accounts}
              />
              {kind !== 'bool' && (
                <span className="text-xs text-muted-foreground">
                  {kind === 'assignee' ? 'Nobody unassigns.' : 'Left empty, the field is cleared.'}
                </span>
              )}
            </div>
          )}
          {every && (
            <label className="grid gap-1">
              <span className="font-medium">Note (optional)</span>
              <Textarea rows={2} value={note} onChange={(event) => setNote(event.target.value)} />
            </label>
          )}
          {tooMany && (
            <p role="alert" className="text-destructive">
              A mass update takes at most {MASS_UPDATE_LIMIT.toLocaleString('en-US')} rows — narrow the list first.
            </p>
          )}
          {said && (
            <p role="alert" className="text-destructive">
              {said}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={sending} onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={!field || sending || tooMany} onClick={() => void apply()}>
            {every
              ? sending
                ? 'Setting…'
                : `Set on ${countOf(total, 'row', 'rows')}`
              : mode === 'assign'
                ? `Assign ${countOf(rows.length, 'row', 'rows')}`
                : 'Stage the change'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
